import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Input from "./Input";
import Select from "./Select";
import {
  getFieldDate,
  getFieldText,
  getFieldName,
  updateEntity,
  createEntity,
} from "../../scripts/helperScripts";
import {
  getBedsWithBedAppointments,
  getClientDiagnosisDetails,
  getProgramServiceItemGroup,
  getProgramServiceItems,
  getProgramUnitBeds,
  getServiceItemStatuses,
} from "../services/soar";

function BedAppointmentModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  initialData,
  clientProgram,
  bedAppointments,
  updateBedAppointments,
}) {
  const clientProgramAdmitDate = new Date(
    clientProgram && getFieldDate(clientProgram.ccx_admitdate)
  );
  const [bedAppointment, setBedAppointment] = useState({});
  const [allBeds, setAllBeds] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);
  const [bedOptionsLoaded, setBedOptionsLoaded] = useState(false);
  const [diagnosisOptions, setDiagnosisOptions] = useState([]);
  const [serviceItemOptions, setServiceItemOptions] = useState([]);
  const [serviceItemOptionsLoading, setServiceItemOptionsLoading] =
    useState(false);
  const [serviceStatusOptions, setServiceStatusOptions] = useState([]);
  const [serviceStatusOptionsLoading, setServiceStatusOptionsLoading] =
    useState(false);
  const [selectedServiceItem, setSelectedServiceItem] = useState("");
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = {
    ccx_bedappointmentid: Joi.string(),
    ccx_bed: Joi.string().required().label("Bed"),
    ccx_serviceitem: Joi.string().label("Service Item"),
    ccx_servicestatuslookup: Joi.string().required().label("Service Status"),
    ccx_clientdiagnosis: Joi.string().required().label("Client Diagnosis"),
    ccx_startdate: Joi.date()
      .label("Start Date")
      .min(moment(clientProgramAdmitDate).subtract(1, "day").toDate())
      .max(
        moment(clientProgramAdmitDate).isAfter(moment())
          ? moment(clientProgramAdmitDate).toDate()
          : "now"
      )
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Start Date cannot be future dated beyond ${moment(
                error.context.limit
              ).format("L")}`;
              break;
            case "date.min":
              error.message = `Start Date cannot be before ${moment(
                error.context.limit
              )
                .add(1, "d")
                .format("L")}`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    ccx_enddate: Joi.date()
      .label("End Date")
      .min(
        moment(
          initialData?.ccx_startdate ||
            bedAppointment.ccx_startdate ||
            clientProgramAdmitDate
        ).toDate()
      )
      .max(
        moment(clientProgramAdmitDate).isAfter(moment())
          ? moment(clientProgramAdmitDate).toDate()
          : "now"
      )
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `End Date cannot be future dated beyond ${moment(
                error.context.limit
              ).format("L")}`;
              break;
            case "date.min":
              error.message = `End Date cannot be before ${moment(
                error.context.limit
              )
                .add(1, "d")
                .format("L")}`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    bedName: Joi.string(),
    serviceItemName: Joi.string(),
    serviceStatusName: Joi.string(),
    diagnosisName: Joi.string(),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (initialData) {
      setBedAppointment(initialData);
      setSelectedServiceItem(initialData.ccx_serviceitem);

      const optionsBed = [];
      optionsBed.push({
        _id: initialData.ccx_bed,
        name: initialData.bedName,
      });
      setBedOptions(optionsBed);

      const optionsServiceItems = [];
      optionsServiceItems.push({
        _id: initialData.ccx_serviceitem,
        name: initialData.serviceItemName,
      });
      setServiceItemOptions(optionsServiceItems);

      const optionsServiceStatus = [];
      optionsServiceStatus.push({
        _id: initialData.ccx_servicestatuslookup,
        name: initialData.serviceStatusName,
      });
      setServiceStatusOptions(optionsServiceStatus);

      if (initialData.ccx_clientdiagnosis) {
        const optionsDiagnosis = [];
        optionsDiagnosis.push({
          _id: initialData.ccx_clientdiagnosis,
          name: initialData.diagnosisName,
        });
        setDiagnosisOptions(optionsDiagnosis);
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (isOpen && clientProgram !== undefined) {
      getClientDiagnosisDetails(getFieldText(clientProgram.ccx_patient)).then(
        (response) => {
          if (response && isMountedRef.current) {
            const diagnosisIds = [];
            const options = [];

            response
              .filter(
                (detail) =>
                  detail["ccx_clientdiagnosis"] !== undefined &&
                  getFieldText(detail.ccx_clientprogram) ===
                    clientProgram.ccx_client_programid
              )
              .forEach((detail) => {
                if (
                  !diagnosisIds.includes(
                    getFieldText(detail["ccx_clientdiagnosis"])
                  )
                ) {
                  diagnosisIds.push(
                    getFieldText(detail["ccx_clientdiagnosis"])
                  );
                  options.push({
                    _id: getFieldText(detail["ccx_clientdiagnosis"]),
                    name: detail["dx.ccx_name"],
                  });
                }
              });

            setDiagnosisOptions(options);

            if (initialData === undefined && options.length === 1) {
              setBedAppointment((ba) => {
                return { ...ba, ccx_clientdiagnosis: options[0]._id };
              });
            }
          }
        }
      );
    }
  }, [clientProgram, initialData, isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (clientProgram.ccx_programunit) {
        getProgramUnitBeds(
          getFieldText(clientProgram.ccx_programunit),
          safyInfo.user.safyInfo.BusinessUnitIdName
        ).then((response) => {
          if (response && isMountedRef.current) setAllBeds(response);
        });

        setServiceItemOptionsLoading(true);
        getProgramServiceItemGroup(
          getFieldText(clientProgram.ccx_programunit),
          safyInfo.user.safyInfo.BusinessUnitIdName
        ).then((response) => {
          if (isMountedRef.current && response && response[0])
            getProgramServiceItems(
              response[0].ccx_cost_center_categoryid,
              safyInfo.user.safyInfo.BusinessUnitIdName
            ).then((response2) => {
              if (response2 && isMountedRef.current) {
                const options = response2
                  .filter((item) => getFieldName(item.ccx_needs_bed) === "Yes")
                  .map((item) => ({
                    _id: item.ccx_service_itemid,
                    name: item.ccx_name,
                  }));

                setServiceItemOptions(options);
                setServiceItemOptionsLoading(false);

                if (options.length === 1) {
                  setSelectedServiceItem(options[0]._id);
                }
              }
            });
        });
      }
    }
  }, [clientProgram, isOpen]);

  useEffect(() => {
    setServiceStatusOptionsLoading(true);
    if (selectedServiceItem !== "") {
      getServiceItemStatuses(selectedServiceItem).then((response) => {
        if (isMountedRef.current) {
          if (response) {
            const options = response
              .filter(
                (status) =>
                  status.ccx_status === "Occupied" ||
                  status.ccx_status === "Attended"
              )
              .map((status) => ({
                _id: status.ccx_servicestatusid,
                name: status.ccx_status,
              }));

            setBedAppointment((ba) => {
              const obj = { ...ba };

              if (
                obj.ccx_servicestatuslookup ===
                "{315A3B59-0AEB-E211-90BC-005056822045}"
              ) {
                options.push({
                  _id: "{315A3B59-0AEB-E211-90BC-005056822045}", // Discharged
                  name: "Discharged",
                });
              }
              if (
                obj.ccx_servicestatuslookup === undefined &&
                options.length === 1
              ) {
                obj.ccx_servicestatuslookup = options[0]._id;
              }

              return obj;
            });

            setServiceStatusOptions(options);
          }

          setServiceStatusOptionsLoading(false);
        }
      });
    }
  }, [selectedServiceItem]);

  useEffect(() => {
    if (
      bedAppointment.ccx_bedappointmentid === undefined &&
      bedAppointment.ccx_startdate !== undefined
    ) {
      const obj = { ...bedAppointment };
      obj.ccx_bed = "";
      setBedAppointment(obj);
      setBedOptions([]);
      setBedOptionsLoaded(false);
    }
  }, [bedAppointment.ccx_startdate]);

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
    }
    const data = { ...bedAppointment };
    data[input.name] = input.value;
    setBedAppointment(data);

    setErrors(errs);
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(bedAppointment, joiSchema, options);

    let validationErrors = {};
    if (error) {
      for (let item of error.details)
        validationErrors[item.path[0]] = item.message;
    }
    if (selectedServiceItem === "")
      validationErrors.ccx_serviceitem = '"Service Item" is required';

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) return null;
    return validationErrors;
  };

  const handleSave = () => {
    const obj = {
      ccx_client: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: getFieldText(clientProgram.ccx_patient),
        },
      },
      ccx_bed: {
        type: "entity",
        value: {
          entityName: "ccx_bed",
          entityId: bedAppointment.ccx_bed,
        },
      },
      ccx_clientprogram: {
        type: "entity",
        value: {
          entityName: "ccx_client_program",
          entityId: clientProgram.ccx_client_programid,
        },
      },
      ccx_serviceitem: {
        type: "entity",
        value: {
          entityName: "ccx_service_item",
          entityId: selectedServiceItem,
        },
      },
      ccx_servicestatuslookup: {
        type: "entity",
        value: {
          entityName: "ccx_servicestatus",
          entityId: bedAppointment.ccx_servicestatuslookup,
        },
      },
      ccx_actualadmissiondate: {
        type: "datetime",
        value: new Date(bedAppointment.ccx_startdate),
      },
      ccx_startdate: {
        type: "datetime",
        value: new Date(bedAppointment.ccx_startdate),
      },
    };

    if (
      bedAppointment.ccx_clientdiagnosis !== undefined &&
      bedAppointment.ccx_clientdiagnosis !== ""
    ) {
      obj.ccx_clientdiagnosis = {
        type: "entity",
        value: {
          entityName: "ccx_clientdiagnosis",
          entityId: bedAppointment.ccx_clientdiagnosis,
        },
      };
    }

    if (bedAppointment.ccx_enddate) {
      const endDate = new Date(bedAppointment.ccx_enddate);
      obj.ccx_servicestatuslookup = {
        type: "entity",
        value: {
          entityName: "ccx_servicestatus",
          entityId: "{315A3B59-0AEB-E211-90BC-005056822045}", // Discharged
        },
      };
      obj.ccx_enddate = {
        type: "datetime",
        value: new Date(
          Date.UTC(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate(),
            23,
            59
          )
        ),
      };
      obj.ccx_actualdischargedate = {
        type: "datetime",
        value: new Date(
          Date.UTC(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate(),
            23,
            59
          )
        ),
      };
    }

    if (bedAppointment.ccx_bedappointmentid !== undefined) {
      const toastId = toast.loading("Bed Appointment is being updated...", {
        type: toast.TYPE.INFO,
      });
      updateEntity(
        "ccx_bedappointment",
        bedAppointment.ccx_bedappointmentid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          toast.update(toastId, {
            render: "Bed Appointment has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          updateBedAppointments && updateBedAppointments();
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Bed Appointment.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    } else {
      const toastId = toast.loading("Bed Appointment is being created...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "ccx_bedappointment",
        JSON.stringify(obj),
        safyInfo,
        getFieldName(clientProgram.ccx_company).split(" of ")[1]
      ).then(() => {
        if (bedAppointments) {
          const prevAppointment = bedAppointments.filter(
            (appointment) =>
              appointment.ccx_actualdischargedate === undefined &&
              getFieldText(appointment.ccx_clientprogram) ===
                clientProgram.ccx_client_programid
          )[0];

          if (prevAppointment !== undefined) {
            const startDate = new Date(bedAppointment.ccx_startdate);
            let endDate = startDate;
            const prevAdmissionDate = new Date(
              prevAppointment.ccx_actualadmissiondate
            );
            if (prevAdmissionDate > endDate) endDate = prevAdmissionDate;

            const prevAppointmentObj = {
              ccx_enddate: {
                type: "datetime",
                value: new Date(
                  Date.UTC(
                    endDate.getFullYear(),
                    endDate.getMonth(),
                    endDate.getDate(),
                    23,
                    59
                  )
                ),
              },
              ccx_actualdischargedate: {
                type: "datetime",
                value: new Date(
                  Date.UTC(
                    endDate.getFullYear(),
                    endDate.getMonth(),
                    endDate.getDate(),
                    23,
                    59
                  )
                ),
              },
              ccx_servicestatuslookup: {
                type: "entity",
                value: {
                  entityName: "ccx_servicestatus",
                  entityId: "{315A3B59-0AEB-E211-90BC-005056822045}",
                },
              },
            };
            toast.update(toastId, {
              render:
                "Bed Appointment has been created. Discharging the previous Bed Appointment...",
              type: toast.TYPE.INFO,
            });
            updateEntity(
              "ccx_bedappointment",
              prevAppointment.ccx_bedappointmentid,
              JSON.stringify(prevAppointmentObj),
              safyInfo
            )
              .then(() => {
                toast.update(toastId, {
                  render: "The previous Bed Appointment has been discharged.",
                  type: toast.TYPE.SUCCESS,
                  isLoading: false,
                  closeOnClick: true,
                });
                updateBedAppointments && updateBedAppointments();
              })
              .catch(() => {
                toast.update(toastId, {
                  render:
                    "An error occurred while discharging the previous Bed Appointment.",
                  type: toast.TYPE.ERROR,
                  isLoading: false,
                  autoClose: 5000,
                  closeOnClick: true,
                });
              });
          } else {
            toast.update(toastId, {
              render: "Bed Appointment has been created.",
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
            updateBedAppointments && updateBedAppointments();
          }
        } else
          toast.update(toastId, {
            render: "Bed Appointment has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        updateBedAppointments && updateBedAppointments();
      });
    }
    handleHideModal();
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const resetForm = () => {
    const obj = {};

    if (serviceItemOptions.length === 1) {
      obj.ccx_serviceitem = serviceItemOptions[0]._id;
      if (serviceStatusOptions.length === 1) {
        obj.ccx_servicestatuslookup = serviceStatusOptions[0]._id;
      }
    } else setServiceStatusOptions([]);

    setBedAppointment(obj);
    setBedOptions([]);
    setBedOptionsLoaded(false);
    setServiceStatusOptionsLoading(false);
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const handleSearch = () => {
    setBedOptionsLoaded(false);
    getBedsWithBedAppointments(
      getFieldText(clientProgram.ccx_programunit),
      bedAppointment.ccx_startdate,
      safyInfo.user.safyInfo.BusinessUnitIdName
    ).then((response) => {
      if (isMountedRef.current) {
        const options = [];

        if (response) {
          const bedIds = response.map((bed) => bed.ccx_bedid);
          allBeds.forEach((bed) => {
            if (!bedIds.includes(bed.ccx_bedid))
              options.push({ _id: bed.ccx_bedid, name: bed.ccx_name });
          });
        } else {
          allBeds.forEach((bed) => {
            options.push({ _id: bed.ccx_bedid, name: bed.ccx_name });
          });
        }

        setBedOptions(options);
        setBedOptionsLoaded(true);
      }
    });
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} hideModal={handleHideModal} animation={false}>
          <Modal.Header>
            <Modal.Title>Bed Appointment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container">
              <div className="row">
                <div className="col">
                  {clientProgram === undefined ? (
                    <div>Client Program is being created...</div>
                  ) : null}
                  <Input
                    name="ccx_startdate"
                    label="Admission Date"
                    type="date"
                    value={bedAppointment.ccx_startdate}
                    onChange={handleChange}
                    error={errors.ccx_startdate}
                    disabled={
                      bedAppointment.ccx_bedappointmentid ||
                      clientProgram === undefined
                    }
                  />
                </div>
              </div>
              <div className="row pb-2">
                <div className="col-3" style={{ marginTop: "30px" }}>
                  <button className="btn btn-primary" onClick={handleSearch}>
                    Search
                  </button>
                </div>
                <div className="col">
                  <Select
                    name="ccx_bed"
                    label="Bed"
                    options={bedOptions}
                    value={bedAppointment.ccx_bed}
                    onChange={handleChange}
                    error={errors.ccx_bed}
                    disabled={
                      !bedOptionsLoaded || bedAppointment.ccx_bedappointmentid
                    }
                  />
                </div>
              </div>
            </div>
            <Select
              name="ccx_serviceitem"
              label="Service Item"
              options={serviceItemOptions}
              value={selectedServiceItem}
              onChange={(e) => {
                const errs = { ...errors };
                delete errs.ccx_serviceitem;
                setErrors(errs);
                setSelectedServiceItem(e.currentTarget.value);
              }}
              error={errors.ccx_serviceitem}
              disabled={
                bedAppointment.ccx_bedappointmentid || serviceItemOptionsLoading
              }
            />
            <Select
              name="ccx_servicestatuslookup"
              label="Service Status"
              options={serviceStatusOptions}
              value={bedAppointment.ccx_servicestatuslookup}
              onChange={handleChange}
              error={errors.ccx_servicestatuslookup}
              disabled={
                bedAppointment.ccx_bedappointmentid ||
                serviceStatusOptionsLoading
              }
            />
            <Select
              name="ccx_clientdiagnosis"
              label="Client Diagnosis"
              options={diagnosisOptions}
              value={bedAppointment.ccx_clientdiagnosis}
              onChange={handleChange}
              error={errors.ccx_clientdiagnosis}
            />
            <Input
              name="ccx_enddate"
              label="Discharge Date"
              type="date"
              value={bedAppointment.ccx_enddate}
              onChange={handleChange}
              error={errors.ccx_enddate}
              disabled={clientProgram === undefined}
            />
          </Modal.Body>
          <Modal.Footer>
            <Link
              className="btn btn-success"
              id="BDS"
              title="Open Bed Day Statuses"
              to="/BDS"
              state={{
                contactName: clientProgram?.["con.fullname"],
                bedAppointmentId: bedAppointment.ccx_bedappointmentid,
                bedName: bedAppointment.bedName,
              }}
            >
              BDS
            </Link>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={handleHideModal}>
                  Cancel
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSave();
                  }}
                >
                  Submit
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    nextModal();
                  }}
                >
                  Skip
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      resetForm();
                      nextModal();
                    }
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default BedAppointmentModal;
